import { withCustomAlert, withLoading, withSidebarRefreshHandler } from "hoc";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle
} from "reactstrap";
import { FilterDateRange, SCDropDown, Search } from "../../../../components";
import { Webservice } from "../../../../services";
import customerListStyles from "../styles.module.scss";

const VccApplicationList = props => {
  const filterDropdownOptions = [
    { value: "ProfileId", label: "Profile Id" },
  ];

  const filterStatusOptions = [
    { value: "All", label: "All" },
    { value: "Y", label: "Enrolled" },
    { value: "N", label: "Not Enrolled" },
  ];

  const getInitialFromDate = () => {
    return sessionStorage.getItem("fromDate")
      ? moment(sessionStorage.getItem("fromDate"))
      : moment();
  };

  const getInitialToDate = () => {
    return sessionStorage.getItem("toDate")
      ? moment(sessionStorage.getItem("toDate"))
      : moment();
  };

  const [selectedFilter, setSelectedFilter] = useState(null);
  const [keyword, setKeyword] = useState(null);
  const [fromDate, setFromDate] = useState(getInitialFromDate());
  const [toDate, setToDate] = useState(getInitialToDate());
  const [totalPage, setTotalPage] = useState(1);
  const [page, setPage] = useState(0);
  const [size] = useState(100);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [applicationList, setApplicationList] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [downloadData, setDownloadData] = useState([]);
  const [csvLink, setCsvLink] = useState(null);

  const getDashboardList = isDownloadCsv => {
    if (fromDate && toDate) {
      props.showLoading();

      let request = {
        from_date: fromDate,
        to_date: toDate,
        page: page,
        size: size
      };
      if (selectedFilter) {
        switch (selectedFilter.value) {
          case "ProfileId":
            request = { ...request, partner_profile_id: keyword };
            break;

        }
      }
      if (selectedStatus && selectedStatus.value !== "All") {
        request = { ...request, is_enroll: selectedStatus.value };
      }

      if (!isDownloadCsv) {
        props.hideLoading();
        Webservice.applications.getVccApplications({
          ...request
        })
          .then(response => {
            setTotalPage(
              Math.ceil(response.headers.get("X-Total-Count") / size)
            );

            response.json().then(r => {
              setApplicationList(r);
              props.hideLoading();
            });
          })
          .catch(error => {
            console.log("application list error: ", error);
            if (error.response && error.response.error) {
              props.alert(error.response.error);
              props.hideLoading();
            }
          });
      } else {
        Webservice.applications.getVccApplicationsCsv({ ...request })
          .then(response => {
            response.text().then(r => {
              setDownloadData(r);
              csvLink.link.click();
              props.hideLoading();
            });
          })
          .catch(error => {
            console.log("application list csv error: ", error);
            if (error.response && error.response.error) {
              props.alert(error.response.error);
              props.hideLoading();
            }
          });
      }
    }
  };

  useEffect(() => {
    getDashboardList();
  }, [fromDate, toDate, page, selectedStatus]);

  const dropdownOnChange = selectedOption => {
    setSelectedFilter(selectedOption);
  };

  const inputOnChange = input => {
    setKeyword(input);
  };

  const onSelectedDateRangeChange = data => {
    setSelectedDateRange(data);

    if (data) {
      const today = moment();
      const minusSevenDays = moment().subtract(6, "days");
      setToDate(today);

      if (data === "today") {
        setFromDate(today);
      } else if (data === "thisWeek") {
        setFromDate(minusSevenDays);
      }
    }
  };

  useEffect(() => {
    const today = moment().dayOfYear();
    const minusSevenDays = moment()
      .subtract(6, "days")
      .dayOfYear();

    if (fromDate && toDate) {
      if (fromDate.dayOfYear() === today && toDate.dayOfYear() === today) {
        setSelectedDateRange("today");
      } else if (
        fromDate.dayOfYear() === minusSevenDays &&
        toDate.dayOfYear() === today
      ) {
        setSelectedDateRange("thisWeek");
      } else {
        setSelectedDateRange(null);
      }

      sessionStorage.setItem("fromDate", fromDate);
      sessionStorage.setItem("toDate", toDate);
    }
  }, [fromDate, toDate]);

  const onFromDate = fromDate => {
    setFromDate(fromDate);
  };

  const onToDate = toDate => {
    setToDate(toDate);
  };

  const toggleFilterByDropdown = () => {
    setIsOpen(!isOpen);
  };

  const onFilterStatusChange = event => {
    const value = event.currentTarget.value;
    const name = event.currentTarget.name;
    const selectedOption = {
      value: value,
      label: name
    };

    setSelectedStatus(selectedOption);
  };

  const getStatusPillClass = status => {
    let cls = customerListStyles.statusPill;
    switch (status) {
      case "APP":
      case "ACT":
        cls = cls + " " + customerListStyles.approved;
        return cls;

      case "REJ":
      case "SUB":
      case "PROC":
      default:
        cls = cls + " " + customerListStyles.rejected;
        return cls;
    }
  };

  const getStatusPillLabel = status => {
    switch (status) {
      case "APP":
      case "ACT":
        return "Enrolled";

      case "REJ":
      case "SUB":
      case "PROC":
      default:
        return "Not Enrolled";
    }
  };

  return (
    <div className={customerListStyles.section}>
      <div className={customerListStyles.filterBar}>
        <h1>Search Virtual Credit Card Customers</h1>

        <div className={customerListStyles.filters}>
          <div className={customerListStyles.filtersLeft}>
            <SCDropDown
              options={filterDropdownOptions}
              onChange={dropdownOnChange}
              selectedOption={selectedFilter}
            />

            <Search
              onChange={inputOnChange}
              onClickButton={() => getDashboardList(false)}
            />
          </div>

          <FilterDateRange
            startDate={fromDate}
            endDate={toDate}
            onFromDate={onFromDate}
            onToDate={onToDate}
            showContainer
            handleClickToday={data => onSelectedDateRangeChange(data)}
            handleClickThisWeek={data => onSelectedDateRangeChange(data)}
            handleClear={data => onSelectedDateRangeChange(data)}
            selectedOption={selectedDateRange}
          />
        </div>
      </div>

      <div className={customerListStyles.secondaryFilters}>
        <div className={customerListStyles.filterByStatus}>
          <label>Filter by</label>
          <Dropdown isOpen={isOpen} toggle={toggleFilterByDropdown} className={customerListStyles.dropdown}>
            <DropdownToggle caret className={customerListStyles.dropdownToggle}>
              {selectedStatus ? selectedStatus.label : "Please select"}
            </DropdownToggle>

            <DropdownMenu>
              {filterStatusOptions.map(option => {
                return (
                  <DropdownItem
                    key={option.value}
                    onClick={onFilterStatusChange}
                    value={option.value}
                    name={option.label}
                  >
                    {option.label}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </Dropdown>
        </div>

        <div className={customerListStyles.exportCsv}>
          <Button
            type="button"
            className={customerListStyles.primaryOutlined}
            onClick={() => getDashboardList(true)}
          >
            Export CSV
          </Button>
        </div>
        <div>
          <CSVLink
            data={downloadData}
            filename="vcc_application.csv"
            className="hidden"
            ref={r => setCsvLink(r)}
            target="_blank"
          />
        </div>
      </div>

      <div className={customerListStyles.customerList}>
        <div className={customerListStyles.tableContainer}>
          <table className={customerListStyles.table}>
            <thead>
              <tr>
                <th>Date Submitted</th>
                <th>Date Enrolled</th>
                <th>Profile ID</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {applicationList.map((item,i) => {
                return (
                  <tr key={"vcc-app-"+i}>
                    <td>{!!item.submittedOn ? moment(item.submittedOn).format("DD/MM/YYYY") : "-"}</td>
                    <td>{!!item.dateEnrolled ? moment(item.dateEnrolled).format("DD/MM/YYYY") : "-"}</td>
                    <td>{item.partnerProfileId || "-"}</td>
                    <td>
                      <span
                        className={getStatusPillClass(item.onboardingStatus)}
                      >
                        {getStatusPillLabel(item.onboardingStatus)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPage > 0 && (
          <nav className={customerListStyles.nav}>
            <ul className={'pagination'}>
              <li className={`page-item${page === 0 ? " disabled" : ""}`}>
                <a className="page-link" onClick={() => setPage(0)}>
                  First
                </a>
              </li>

              <li className={`page-item${page === 0 ? " disabled" : ""}`}>
                <a className="page-link" onClick={() => setPage(page - 1)}>
                  Previous
                </a>
              </li>

              {[...Array(totalPage).keys()].map(a => (
                <li
                  key={a}
                  className={`page-item${page === a ? " active" : ""}`}
                >
                  <a className="page-link" onClick={() => setPage(a)}>
                    {a + 1}
                  </a>
                </li>
              ))}

              <li
                className={`page-item${
                  page === totalPage - 1 ? " disabled" : ""
                }`}
              >
                <a className="page-link" onClick={() => setPage(page + 1)}>
                  Next
                </a>
              </li>

              <li
                className={`page-item${
                  page === totalPage - 1 ? " disabled" : ""
                }`}
              >
                <a className="page-link" onClick={() => setPage(totalPage - 1)}>
                  Last
                </a>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
};

export default withSidebarRefreshHandler(
  withLoading(withCustomAlert(VccApplicationList))
);
